import { gunzipSync, gzipSync } from 'fflate';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PlayerSize } from '../../components/player/player';
import StartupErrorComponent from '../../components/settingsAndStartup/startupErrorComponent';
import getScrollbarWidth from '../../gUtilities/getScrollbarWidth';
import storageAvailable from '../../gUtilities/storageAvailable';
import SyncType from '../../models/playlist/syncType';
import RepeatState from '../../models/repeatState';
import { StorageProviderKey, StorageProviderMap } from '../../models/storageProviderKeys';
import SyncProvider from '../../models/synchronization/syncProvider';
import SyncProviders from '../../models/synchronization/syncProviders';
import { reqQueueCreator } from '../queues/reqQueue';
import * as serviceWorker from '../serviceWorker/serviceWorker';
import IStorageProvider from '../storage/istorageProvider';
import { Context, DeviceCapabilities, Flate, SettingsState } from './context';
import createCallSnackbar from './snackbar';

interface Props {
  children: React.ReactNode;
}

/** iOS can leave service worker registration pending indefinitely. */
const swRegistrationTimeoutMs = 10000;

/** Rejects if the wrapped promise has not settled in time. */
function withTimeout<T>(promise: Promise<T>, label: string) {
  let timer: ReturnType<typeof setTimeout>;

  return Promise.race([
    promise,
    new Promise<never>((_resolve, reject) => {
      timer = setTimeout(
        () => reject(new Error(`${label} timed out after ${swRegistrationTimeoutMs}ms`)),
        swRegistrationTimeoutMs,
      );
    }),
  ]).finally(() => clearTimeout(timer));
}

export default function ContextService({
  children,
}: Props) {

  console.debug('Rendering ctx');

  const [dndData] = useState({});

  const [deviceCapabilities, setDeviceCapabilities]
    = useState<DeviceCapabilities>(new DeviceCapabilities());

  // type 'cast' is okey as map is typed to return provider | undefined
  const [storageProviderMap] = useState<StorageProviderMap>({} as StorageProviderMap);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const settingsLoading = useRef(false);
  const [settings, setSettings] = useState(() => new SettingsState());

  const [playerSize, setPlayerSize] = useState(PlayerSize.Hide);

  const [snackbarEl, setSnackbarEl] = useState<HTMLDivElement | null>(null);
  const callSnackbar = useMemo(() => createCallSnackbar(snackbarEl), [snackbarEl]);

  /**
   * gzip codec. Statically imported and always present.
   *
   * This used to be an async `import('wasm-flate')` that gated startup, on the
   * reasoning that a WebAssembly module takes time to fetch and compile and
   * might fail. fflate is a few KB of ordinary JavaScript in the main bundle -
   * if it were missing, nothing else would be running either - so there is
   * nothing left to wait for and nothing left to report.
   */
  const [flate] = useState<Flate>(() => ({
    gzip_encode_raw: gzipSync,
    gzip_decode_raw: gunzipSync,
  }));
  /**
   * The reason for the ref like state is because the sp's themselves are continuously updated
   * This happens in their respective service by assigning to a stable sp ref obj
   * 
   * If these updates would setState it would at worst cause an infinite loop and at best alot of re-renders
   * This way we allow frequent updates but ensure that we always have the latest version in context.
   * 
   * Other implementations are likely to end up with stale ctx sp here and/or on the context value
   */
  const [storageProvider, setStorageProvider] = useState({
    current: undefined as IStorageProvider | undefined,
  });
  const [syncProviders, setSyncProviders] = useState<SyncProviders>({});
  // We use a set of currently processing items, this ensures that if there are multiple concurrent tasks
  // One task completing will not remove the processing state for all the others
  const [runningTasks, setRunningTask] = useState<React.MutableRefObject<Set<string>>>({
    current: new Set<string>()
  });
  const setRunningTaskF = useCallback((task: string) => setRunningTask(prev => ({
    current: prev.current.add(task)
  })), []);
  const removeRunningTaskF = useCallback((task: string) =>
    setRunningTask(prev => {
      prev.current.delete(task);
      return {
        current: prev.current
      };
    }), []);
  // Signifies that the queue is currently actively processing a request
  const setActive = useRef(setRunningTaskF);
  setActive.current = setRunningTaskF;
  const disableActive = useRef(removeRunningTaskF);
  disableActive.current = removeRunningTaskF;

  const spLogoutHandling = useRef<() => Promise<void>>();

  /** The base request queue that all others build on */
  const [unifiedReqQueue] = useState(() => reqQueueCreator(
    () => setActive.current('reqQueue'),
    () => disableActive.current('reqQueue'),
  ));
  useEffect(function beforeUnloadWarning() {

    function beforeUnloadCallback(ev: BeforeUnloadEvent) {

      if (runningTasks.current.size > 0 &&
        !(runningTasks.current.has('resetAppDataFolder') && runningTasks.current.size === 1)) {

        const prompt = 'There are currently running tasks, are you sure you would like to exit?';

        ev.preventDefault();
        ev.returnValue = prompt;
        return prompt;
      }
    }

    if (runningTasks.current.size) {
      window.addEventListener('beforeunload', beforeUnloadCallback);
    }

    return function () {
      window.removeEventListener('beforeunload', beforeUnloadCallback);
    };
  }, [runningTasks]);

  useEffect(function ensureCapableDevice() {

    /**
     * Registers the worker and waits for it to become active.
     *
     * This deliberately never reloads the page. It used to: a freshly
     * registered worker does not control the page that registered it, so the
     * app called window.location.reload() to pick it up. Unguarded, that is an
     * infinite reload loop on any device where the worker fails to take
     * control - and it never renders a single frame, which is exactly what a
     * completely broken app looks like. iOS hits this regularly, since ITP
     * evicts service workers after about a week.
     *
     * thesw.js now calls skipWaiting()/clients.claim(), so it takes control on
     * its own - which matters, because the worker has to be in control before
     * the first Drive appData download, not merely before playback.
     */
    async function doRegisterSW() {
      // Before registering, not after: the worker calls clients.claim() as
      // soon as it activates and may ask us for a token immediately, while
      // register() is still in flight.
      serviceWorker.initGapiTokenBridge();

      // Both of these can hang indefinitely on iOS. Left unbounded, a hang
      // leaves detectionComplete false forever - a permanently blank page.
      await withTimeout(serviceWorker.register(), 'Service worker registration');
      await withTimeout(navigator.serviceWorker.ready, 'Service worker activation');
    }

    const deviceCapabilities = new DeviceCapabilities();
    deviceCapabilities.hasFetch = 'fetch' in window;
    deviceCapabilities.hasPromise = 'Promise' in window;
    deviceCapabilities.hasTextEncoder = 'TextEncoder' in window;
    deviceCapabilities.hasServiceWorkers = 'serviceWorker' in navigator;
    deviceCapabilities.hasLocalStorage = storageAvailable('localStorage');

    deviceCapabilities.detectionComplete = true;

    if (deviceCapabilities.hasServiceWorkers) {
      doRegisterSW()
        .then(() => setDeviceCapabilities(deviceCapabilities))
        .catch(err => {
          console.error('Service worker registration failed', err);

          // Start up anyway rather than refusing to render.
          //
          // Note this is NOT harmless where Google Drive is involved. The
          // worker is the only thing that authenticates bare `?alt=media`
          // requests to drive/v3 - see gapiService.getDriveFileUrl, which
          // returns a URL carrying no credentials at all. Without the worker:
          //   - getOrCreateDataFile 401s, so library/playlists/settings never
          //     load when Drive is the storage provider;
          //   - Drive tracks will not play, whatever the storage provider is.
          // Everything else - YouTube, Google sign-in, gapi.client.* calls,
          // OneDrive, Dropbox, podcasts - is unaffected; those either
          // self-authenticate or play from pre-signed URLs.
          setDeviceCapabilities({
            ...deviceCapabilities,
            hasServiceWorkers: false,
          });
        });
    }
    else {
      setDeviceCapabilities(deviceCapabilities);
    }
  }, []);

  useEffect(function doLoadState() {
    if (snackbarEl
      && flate
      && storageProvider?.current
      && !settingsLoaded
      && !settingsLoading.current) {

      settingsLoading.current = true;

      storageProvider.current.load('settings.js.gz', {})
        .then(
          s => {
            setSettings(new SettingsState(s));
            setSettingsLoaded(true);
          },
          callSnackbar,
        )
        .finally(() => settingsLoading.current = false);
    }
  }, [storageProvider, settingsLoaded, snackbarEl, callSnackbar, flate]);

  const setPersistState = useCallback((settings: SettingsState) => {
    if (storageProvider?.current && flate) {
      storageProvider.current.saveAppData(
        'settings.js.gz',
        settings);
    }
    setSettings(settings);
  }, [storageProvider, flate]);

  const configureStorageProvider = useCallback(function (key?: StorageProviderKey) {
    if (key) {
      const provider = storageProviderMap[key];

      if (provider && provider.current) {

        try {
          localStorage.setItem('storageProvider', provider.current.name);
        }
        catch (err) {
          callSnackbar(err);
        }

        if (storageProvider.current) {
          // Let's not try to be too clever, it's best to start fresh in the extremely uncommon case
          // of a user switching their sp
          window.location.reload();
        } else {
          setStorageProvider(provider);
        }
      } else {
        callSnackbar('Provider hasn\'t registered or unknown provider specified.');
      }
    }
    else {
      try {
        localStorage.removeItem('storageProvider');
      } catch { }
      setStorageProvider({ current: undefined });
    }
  }, [storageProvider, storageProviderMap, callSnackbar]);

  const autoConfigureStorageProvider = useCallback(function () {
    if (!storageProvider.current) {
      try {
        const sp = localStorage.getItem('storageProvider');
        if (sp) {
          configureStorageProvider(sp as StorageProviderKey);
        }
      }
      catch (err) {
        callSnackbar(err);
      }
    }
  }, [storageProvider, configureStorageProvider, callSnackbar]);

  const toggleRepeat = useCallback(function () {
    switch (settings.repeatState) {
      case RepeatState.All:
        setPersistState(
          Object.assign({}, settings, { repeatState: RepeatState.None })
        );
        break;
      case RepeatState.One:
        setPersistState(
          Object.assign({}, settings, { repeatState: RepeatState.All })
        );
        break;
      case RepeatState.None:
      default:
        setPersistState(
          Object.assign({}, settings, { repeatState: RepeatState.One })
        );
        break;
    }
  }, [setPersistState, settings]);

  const addSyncProvider = useCallback(function (syncType: SyncType, syncProvider: SyncProvider) {
    setSyncProviders({
      ...syncProviders,
      [syncType]: syncProvider,
    });
  }, [syncProviders]);

  const contextSpLogoutHandling = useCallback(
    async function () {
      setSettingsLoaded(false);

      if (spLogoutHandling.current) {
        await spLogoutHandling.current();
      }
    }, []);

  const setSpLogoutHandling = useCallback(
    (f: () => Promise<void>) => spLogoutHandling.current = f,
    [],
  );

  const scrollbarWidth = useRef<number>();
  useEffect(function calculateScrollbarWidth() {
    scrollbarWidth.current = getScrollbarWidth();
  }, []);

  const setLibraryPath = useCallback((path: string) => setPersistState(
    Object.assign({}, settings, { libraryPath: path })
  ), [settings, setPersistState]);

  const toggleShowPlaylistStatusBar = useCallback(() => setPersistState({
    ...settings,
    showPlaylistStatusBar: !settings.showPlaylistStatusBar
  }), [
    settings,
    setPersistState,
  ]);

  const toggleConfirmDelete = useCallback(() => setPersistState({
    ...settings,
    confirmDelete: !settings.confirmDelete,
  }), [settings, setPersistState]);

  const toggleGoogleDriveIdNavigation = useCallback(
    () => setPersistState({
      ...settings,
      enableGoogleDriveIdNavigation: !settings.enableGoogleDriveIdNavigation
    }),
    [settings, setPersistState]);
  const toggleLockPlayerSizeMedium = useCallback(function toggleLockPlayerSizeMedium() {
    setPersistState({
      ...settings,
      lockPlayerSizeMedium: !settings.lockPlayerSizeMedium
    });
    setPlayerSize(PlayerSize.Medium);
  }, [settings, setPersistState]);

  const ctxValue = useMemo(() => ({

    deviceCapabilities,

    settingsLoaded,

    playerSize,
    setPlayerSize,

    toggleRepeat,

    setLibraryPath,

    callSnackbar,

    syncProviders,
    addSyncProvider,

    flate,
    storageProvider,
    configureStorageProvider,
    storageProviderMap,
    autoConfigureStorageProvider,

    isProcessing: runningTasks.current.size > 0,
    setRunningTask: setRunningTaskF,
    removeRunningTask: removeRunningTaskF,

    unifiedReqQueue,

    dndData,

    spLogoutHandling: contextSpLogoutHandling,
    setLibrarySpLogoutHandling: setSpLogoutHandling,

    scrollbarWidth: scrollbarWidth.current,

    toggleShowPlaylistStatusBar,

    toggleConfirmDelete,
    toggleGoogleDriveIdNavigation,
    toggleLockPlayerSizeMedium,

    settings,
  }), [
    deviceCapabilities,
    settingsLoaded,
    playerSize,
    settings,
    toggleRepeat,
    callSnackbar,
    syncProviders,
    addSyncProvider,
    flate,
    storageProvider,
    configureStorageProvider,
    storageProviderMap,
    autoConfigureStorageProvider,
    runningTasks,
    setRunningTaskF,
    removeRunningTaskF,
    contextSpLogoutHandling,
    setSpLogoutHandling,
    dndData,
    unifiedReqQueue,
    setLibraryPath,
    toggleShowPlaylistStatusBar,
    toggleConfirmDelete,
    toggleGoogleDriveIdNavigation,
    toggleLockPlayerSizeMedium,
  ]);

  /**
   * Capabilities the app genuinely cannot run without.
   *
   * Deliberately excludes hasServiceWorkers. Treating it as fatal meant a
   * single flaky registration - the common case on iOS, where ITP evicts
   * workers after about a week - blocked even the loading screen from
   * rendering, for every user regardless of storage provider.
   *
   * The worker IS genuinely required for Google Drive `?alt=media` requests
   * (library/playlists/settings downloads, and Drive track playback), so its
   * absence is not universally survivable - but that is a Drive concern, not a
   * reason to refuse to boot a Dropbox or OneDrive user.
   */
  const isCapableDevice = useMemo(
    () => deviceCapabilities.detectionComplete
      && deviceCapabilities.hasFetch
      && deviceCapabilities.hasPromise
      && deviceCapabilities.hasTextEncoder
      && deviceCapabilities.hasLocalStorage
    , [deviceCapabilities]);

  return (
    <>
      {snackbarEl &&
        <Context.Provider value={ctxValue}>
          {isCapableDevice
            ?
            children
            : deviceCapabilities.detectionComplete &&
            <StartupErrorComponent />
          }
        </Context.Provider>
      }
      <div ref={c => setSnackbarEl(c)} className="mdc-snackbar">
        <button
          type="button"
          className="mdc-button mdc-snackbar__action"
        >
          <div className="mdc-snackbar__surface">
            <div className="mdc-snackbar__label"
              role="status"
              aria-live="polite">
            </div>
            <div className="mdc-snackbar__actions">
            </div>
          </div>
        </button>
      </div>
    </>
  );
}
